import React, { useEffect, useMemo, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import TicketListFilters from "./TicketListFilters";
import TicketListItem from "./TicketListItem";

export const TicketListPane = ({
  tickets,
  selectedTicketId,
  filters,
  loading,
  loadingMore,
  error,
  hasMore,
  onSelectTicket,
  onFilterChange,
  onClearFilters,
  onLoadMore,
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const loadMoreRef = useRef(null);

  const statusTabs = useMemo(
    () => [
      { label: "All", value: "" },
      { label: "Open", value: "Open" },
      { label: "In Progress", value: "In Progress" },
      { label: "Resolved", value: "Resolved" },
      { label: "Closed", value: "Closed" },
    ],
    []
  );

  const handleScroll = (event) => {
    const node = event.currentTarget;
    const nearBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 40;
    if (nearBottom && hasMore && !loading && !loadingMore) {
      onLoadMore();
    }
  };

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && hasMore && !loading && !loadingMore) {
          onLoadMore();
        }
      },
      {
        root: target.parentElement,
        rootMargin: "120px",
        threshold: 0.1,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <section className="workspace-left">
      <div className="workspace-left-header">
        <h3>Ticket Support</h3>
        <div className="workspace-left-search-row">
          <InputText
            value={filters.q}
            placeholder="Search Ticket ID"
            onChange={(event) => onFilterChange("q", event.target.value)}
          />
          <button
            type="button"
            className="workspace-filter-toggle"
            onClick={() => setShowFilter((prev) => !prev)}
          >
            <i className="pi pi-filter" />
          </button>
        </div>
        <div className="workspace-status-tabs">
          {statusTabs.map((tab) => (
            <button
              key={tab.label}
              type="button"
              className={`workspace-status-tab ${String(filters.status || "") === String(tab.value) ? "active" : ""}`}
              onClick={() => onFilterChange("status", tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {showFilter ? (
        <TicketListFilters
          filters={filters}
          onChange={onFilterChange}
          onClear={onClearFilters}
          onClose={() => setShowFilter(false)}
        />
      ) : null}

      {error ? <div className="workspace-inline-error">{error}</div> : null}

      <div className="workspace-ticket-list" onScroll={handleScroll}>
        {loading && tickets.length === 0 ? (
          <div className="workspace-placeholder">Loading tickets...</div>
        ) : null}

        {!loading && tickets.length === 0 ? (
          <div className="workspace-placeholder">No tickets found</div>
        ) : null}

        {tickets.map((ticket) => (
          <TicketListItem
            key={ticket.id}
            ticket={ticket}
            selected={Number(selectedTicketId) === Number(ticket.id)}
            onSelect={onSelectTicket}
          />
        ))}

        {loadingMore ? <div className="workspace-loading-more">Loading more...</div> : null}
        {!hasMore && tickets.length > 0 ? (
          <div className="workspace-loading-more">No more tickets</div>
        ) : null}
        <div ref={loadMoreRef} className="workspace-load-trigger" aria-hidden="true" />
      </div>
    </section>
  );
};

export default TicketListPane;
